import {
  Element,
  Foerderung,
  Gewzv,
  Handlungsbereich,
  IBH,
  InitiatorType,
  Kategorie,
  Landkreis,
  MassnahmenTraeger,
  Region,
  Regionalstelle,
  RegionenType,
  Risikogewaesser,
  Rolle,
  Sachstand,
  Schlagwort,
  Status,
  Teilbereich,
  Verwaltungsebene,
  VerwaltungsTraeger,
  Zustaendigkeit,
} from "@/types";
import { DataService } from "./DataService";
import { AxiosResponse } from "axios";
import StorageService from "@/services/libs/StorageService";
import { Ansprechpartner } from "@/types/Ansprechpartner";

class ErklService extends DataService {
  constructor() {
    super();
    this.requestService.setSuccessHandler((r) => this.successHandler(r));
  }

  /**
   * stores requested data into Storage Service
   * @param response AxiosResponse
   * @returns T
   */
  private successHandler<T>(response: AxiosResponse<T>): T {
    const responseUrlArray = response.request.responseURL.split("/");
    const storageKey = responseUrlArray[responseUrlArray.length - 1];
    const storedDataKeys = [
      "ibh",
      "hwp",
      "kategorie",
      "regionalstelle",
      "risikogewaesser",
      "sachstand",
      "verwaltungsebene",
      "zustaendigkeit",
      //"gewzv",
      "handlungsbereiche",
      "landkreise",
      "teilbereiche",
      "status",
      "regionTypes",
      "massnahmekommunen",
      "allekommunen",
      "institutionOptions",
    ];
    if (storedDataKeys.includes(storageKey)) {
      StorageService.set(storageKey, response.data);
    }
    return this.authService.successHandler(response);
  }

  public getIbhDaten(): Promise<IBH[]> {
    const storedData = StorageService.get("ibh");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() => this.requestService.get<IBH[]>("/erklaerung/ibh"));
  }

  public getHwpDaten(): Promise<Element[]> {
    const storedData = StorageService.get("hwp");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() => this.requestService.get<Element[]>("/erklaerung/hwp"));
  }

  public getKategorien(): Promise<Kategorie[]> {
    const storedData = StorageService.get("kategorie");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Kategorie[]>("/erklaerung/kategorie"),
      );
  }

  public getInstitutionen(): Promise<Element[]> {
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Element[]>("/erklaerung/institutionen"),
      );
  }

  public getRegionalstellen(): Promise<Regionalstelle[]> {
    const storedData = StorageService.get("regionalstelle");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Regionalstelle[]>("/erklaerung/regionalstelle"),
      );
  }

  public getRisikogewaesser(): Promise<Risikogewaesser[]> {
    const storedData = StorageService.get("risikogewaesser");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Risikogewaesser[]>(
          "/erklaerung/risikogewaesser",
        ),
      );
  }

  public getSachstaende(): Promise<Sachstand[]> {
    const storedData = StorageService.get("sachstand");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Sachstand[]>("/erklaerung/sachstand"),
      );
  }

  public getVerwaltungsebenen(): Promise<Verwaltungsebene[]> {
    const storedData = StorageService.get("verwaltungsebene");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Verwaltungsebene[]>(
          "/erklaerung/verwaltungsebene",
        ),
      );
  }

  public getSchlagworte(): Promise<Schlagwort[]> {
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Schlagwort[]>("/erklaerung/schlagwort"),
      );
  }

  public getInitiators(initiatorType: any): Promise<Element[]> {
    // check if user is authenticated before request
    if (initiatorType === undefined || initiatorType === null) {
      new Array<Element>();
    }

    const query = initiatorType.id;
    return this.authService.checkAuthentication().then(() =>
      this.requestService.get<Element[]>("/erklaerung/initiators", {
        query,
      }),
    );
  }

  public getAllInitiators(): Promise<Element[]> {
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Element[]>("/erklaerung/initiators/all"),
      );
  }

  public getRegionen(query: any): Promise<Element[]> {
    // check if user is authenticated before request
    return this.authService.checkAuthentication().then(() =>
      this.requestService.get<Region[]>("/erklaerung/regionen", {
        query,
      }),
    );
  }

  public getZustaendigkeiten(): Promise<Zustaendigkeit[]> {
    const storedData = StorageService.get("zustaendigkeit");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Zustaendigkeit[]>("/erklaerung/zustaendigkeit"),
      );
  }

  public getLandkreise(): Promise<Landkreis[]> {
    const storedData = StorageService.get("landkreise");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Landkreis[]>("/erklaerung/landkreise"),
      );
  }

  public getGewzv(): Promise<Gewzv[]> {
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() => this.requestService.get<Gewzv[]>("/erklaerung/gewzv"));
  }

  public getInstitutionOptions(): Promise<Element[]> {
    const storedData = StorageService.get("institutionOptions");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Element[]>("/erklaerung/institution-options"),
      );
  }

  public getKommunenWithFilter(
    agkId: number | string,
    gemeinden: boolean,
    ortsteile: boolean,
  ): Promise<Element[]> {
    // check if user is authenticated before request
    return this.authService.checkAuthentication().then(() =>
      this.requestService.get<Element[]>("/erklaerung/kommunen/filter", {
        agkId,
        gemeinden,
        ortsteile,
      }),
    );
  }

  public getMassnahmeKommunen(): Promise<Element[]> {
    const storedData = StorageService.get("massnahmekommunen");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Element[]>("/erklaerung/massnahmekommunen"),
      );
  }

  public getKonzepteKommunen(agKommune: any): Promise<Element[]> {
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Element[]>(
          "/erklaerung/massnahme/konzept/kommunen?agKommune=" + agKommune,
        ),
      );
  }

  public getKonzepteKommunenByAgk(agk: any): Promise<Element[]> {
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Element[]>(
          "/erklaerung/massnahme/agk/kommunen?agk=" + agk,
        ),
      );
  }

  public getAnsprechpartner(
    institutionId: any,
    ext: number,
  ): Promise<Element[]> {
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Element[]>(
          "/erklaerung/ansprechpartner/institution/" +
            institutionId +
            "?ext=" +
            ext,
        ),
      );
  }

  public loadAnsprechpartner(id: any): Promise<Ansprechpartner> {
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Ansprechpartner>(
          "/erklaerung/ansprechpartner/" + id,
        ),
      );
  }

  public getRollen(): Promise<Rolle[]> {
    const storedData = StorageService.get("rollen");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() => this.requestService.get<Rolle[]>("/erklaerung/rollen"));
  }

  public getFoerderungen(query: string): Promise<Foerderung[]> {
    // check if user is authenticated before request
    return this.authService.checkAuthentication().then(() =>
      this.requestService.get<Foerderung[]>("/erklaerung/foerderungen", {
        query,
      }),
    );
  }

  public getVerwaltungsTraeger(): Promise<VerwaltungsTraeger[]> {
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<VerwaltungsTraeger[]>(
          "/erklaerung/verwaltungstraeger",
        ),
      );
  }

  public getMassnahmenTraeger(): Promise<MassnahmenTraeger[]> {
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<MassnahmenTraeger[]>(
          "/erklaerung/massnahmentraeger",
        ),
      );
  }

  public getInitiatorType(): Promise<InitiatorType[]> {
    const storedData = StorageService.get("initiatorTypes");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<InitiatorType[]>("/erklaerung/initiatorTypes"),
      );
  }

  public getHandlungsBereiche(): Promise<Handlungsbereich[]> {
    const storedData = StorageService.get("handlungsbereiche");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Handlungsbereich[]>(
          "/erklaerung/handlungsbereiche",
        ),
      );
  }

  public getTeilbereiche(): Promise<Teilbereich[]> {
    const storedData = StorageService.get("teilbereiche");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<Teilbereich[]>("/erklaerung/teilbereiche"),
      );
  }

  public getStatus(): Promise<Status[]> {
    const storedData = StorageService.get("status");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() => this.requestService.get<Status[]>("/erklaerung/status"));
  }

  public getRegionTypes(): Promise<RegionenType[]> {
    const storedData = StorageService.get("regionTypes");
    if (storedData) {
      return Promise.resolve(storedData);
    }
    // check if user is authenticated before request
    return this.authService
      .checkAuthentication()
      .then(() =>
        this.requestService.get<RegionenType[]>("/erklaerung/regionTypes"),
      );
  }
}

const erklServiceInstance = new ErklService();

export { erklServiceInstance };
export default ErklService;
